import React, {useEffect, useState} from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { getAccessToken } from '../Auth/AuthStore'

import styles from './ResultPage.module.css'
import paletteIcon from '../../assets/icons/purplePalette.svg'
import pictureIcon from '../../assets/icons/picture.svg'
import aiChipIcon from '../../assets/icons/aiChip.svg'
import defaultCover from '../../assets/images/default.png'
import { AlertModal } from '../AlertModal'

const ResultPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { drawingAnalysisId } = useParams()

  const [activeTab, setActiveTab] = useState('places')
  const tabs = ['places', 'music', 'videos']

  const {
    drawingData: initialDrawingData,
    answers: initialAnswers = [],
    topicId,
    analysisResult: initialAnalysisResult,
    healingCourse: healingCourseContext = null,
    fromProgramResult = false,
    therapyProgramId = null,
  } = location.state || {}

  const [analysisResult, setAnalysisResult] = useState(initialAnalysisResult || null) 
  const [drawingData, setDrawingData] = useState(initialDrawingData || null)         
  const [answers, setAnswers] = useState(initialAnswers)                         
  const [loading, setLoading] = useState(!initialAnalysisResult)                   
  const [error, setError] = useState(null)     
  const [completingCourse, setCompletingCourse] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')  

  const prevTab = () => {
    const idx = tabs.indexOf(activeTab)
    const nextIdx = (idx - 1 + tabs.length) % tabs.length
    setActiveTab(tabs[nextIdx])
  }

  const nextTab = () => {
    const idx = tabs.indexOf(activeTab)
    const nextIdx = (idx + 1) % tabs.length
    setActiveTab(tabs[nextIdx])
  }

  useEffect(() => {
    if (analysisResult) {
      setLoading(false)
      return
    }

    if (!drawingAnalysisId) {
      navigate('/drawing')
      return
    }

    const fetchDetail = async () => {
      try {
        setLoading(true)
        setError(null)

        const token = getAccessToken()

        const res = await axios.get(
          `${import.meta.env.VITE_API_BASE}/api/drawing-analysis/${drawingAnalysisId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )

        setAnalysisResult(res.data.data)
      } catch (e) {
        console.error('그림 분석 상세 조회 실패:', e)
        setError('분석 결과를 불러오는 중 오류가 발생했습니다.')
      } finally {
        setLoading(false)
      }
    }

    fetchDetail()
  }, [drawingAnalysisId, analysisResult, navigate])

  // 🔹 로딩 상태 처리
  if (loading) {
    return (
      <div className={styles.resultPage}>
        <div className={styles.container}>
          <p>분석 결과를 불러오는 중입니다...</p>
        </div>
      </div>
    )
  }

  if (error || !analysisResult) {
    return (
      <div className={styles.resultPage}>
        <div className={styles.container}>
          <p>{error || '분석 결과를 찾을 수 없습니다.'}</p>
          <button
            className={styles.mainBtn}
            onClick={() => navigate('/')}
          >
            메인으로 돌아가기
          </button>
        </div>
      </div>
    )
  }

  const score = analysisResult.totalScore ?? 0
  const maxScore = 100

  const detailedAnalysis = [
      { question: '0 - 20점 : 불안, 우울 🫥', answer: '“심리적으로 많이 지쳐 있는 상태예요. 스스로를 돌봐야 할 때입니다.”' },
      { question: '21 - 40점 : 약간 불안 😔', answer: '“살짝 긴장이나 불안이 느껴질 수 있어요. 마음을 차분히 가라앉히는 게 좋아요.”' },
      { question: '41 - 60점 : 중립 🙂', answer: '“무난한 심리 상태입니다. 일상적인 활동을 무리 없이 할 수 있어요.”' },
      { question: '61 - 80점 : 긍정적 😄', answer: '“마음이 안정돼 있고 긍정적인 에너지가 느껴져요. 새로운 걸 시도해보기 좋아요!”' },
      { question: '81 - 100점 : 안정, 행복 🥰', answer: '“에너지가 넘치고 매우 안정적인 심리 상태입니다. 즐거운 순간을 만끽해보세요!”' },
    ]

  const aiRecommendations = {
    places: analysisResult.placeRecommendations || [],
    music: analysisResult.musicRecommendations || [],
    videos: analysisResult.videoRecommendations || [],
  }

  const title = analysisResult.title || answers[0] || '나의 그림'
  const drawingImage = analysisResult.imageUrl || drawingData

  // 분석 결과 총평 (4개 분석이 하나로 합쳐진 필드)
  const summaryAnalysis = analysisResult.analysisResult || ''

  const handleCompleteCourse = async () => {
    if (!healingCourseContext?.courseId) {
      navigate('/dashboard')
      return
    }

    const drawingId = analysisResult?.drawingId || drawingAnalysisId
    if (!drawingId) {
      setAlertMessage('그림 정보를 찾을 수 없어 코스를 완료할 수 없습니다.')
      setShowAlert(true)
      return
    }

    try {
      setCompletingCourse(true)
      const token = getAccessToken()
      if (!token) throw new Error('토큰이 없습니다.')

      // 데일리 코스 완료 처리
      await axios.patch(
        `${import.meta.env.VITE_API_BASE}/api/courses/${healingCourseContext.courseId}/complete`,
        null,
        {
          params: { drawingId },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      setAlertMessage(`${healingCourseContext.title || '코스'} 완료 처리되었습니다.`)
      setShowAlert(true)
      setTimeout(() => {
        navigate('/dashboard')
      }, 1000)
    } catch (err) {
      console.error('데일리 코스 완료 처리 실패:', err)
      if (err.response?.status === 403) {
        setAlertMessage('코스에 대한 권한이 없습니다.')
      } else if (err.response?.status === 404) {
        setAlertMessage('코스를 찾을 수 없습니다.')
      } else {
        setAlertMessage('코스 완료 처리 중 오류가 발생했습니다.')
      }
      setShowAlert(true)
    } finally {
      setCompletingCourse(false)
    }
  }

  return (
    <div className={styles.resultPage}>
      <div className={styles.container}>
        <header className={styles.pageHeader}>
          <div className={styles.headerIcon}>
            <img src={paletteIcon} alt="palette" />
          </div>
          <h1>심리 분석 결과</h1>
        </header>

        {/* 그림 및 제목 */}
        <section className={styles.drawingSection}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionIcon}>
              <img src={pictureIcon} alt="picture" />
            </span>
            <h2>분석한 그림 : {title}</h2>
          </div>
          <div className={styles.drawingContainer}>
            <img 
              src={drawingImage} 
              alt="분석된 그림"
              className={styles.drawingImage}
            />
          </div>
        </section>

        {/* 점수 */}
        <section className={styles.scoreSection}>
          <div className={styles.scoreCard}>
            <div className={styles.scoreCircle}>
              <svg viewBox="0 0 200 200" className={styles.scoreSvg}>
                <circle
                  cx="100"
                  cy="100"
                  r="85"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="15"
                />
                <circle
                  cx="100"
                  cy="100"
                  r="85"
                  fill="none"
                  stroke="#7c3aed"
                  strokeWidth="15"
                  strokeDasharray={`${(score / maxScore) * 534} 534`}
                  strokeDashoffset="0"
                  transform="rotate(-90 100 100)"
                  strokeLinecap="round"
                />
              </svg>
              <div className={styles.scoreText}>
                <span className={styles.scoreNumber}>{score}</span>
                <span className={styles.scoreTotal}>/{maxScore}</span>
              </div>
            </div>
            <h3 className={styles.scoreTitle}>나의 종합 심리 점수</h3>
            <p className={styles.scoreDescription}>안정적인 심리 상태입니다!</p>
          </div>

          <div className={styles.analysisCards}>
            <h3 className={styles.analysisTitle}>점수별 심리 상태 구분</h3>
            {detailedAnalysis.map((item, index) => {
              const rangeMatch = item.question.match(/(\d+)\s*-\s*(\d+)점/)
              const isActive = rangeMatch && 
                score >= parseInt(rangeMatch[1]) && 
                score <= parseInt(rangeMatch[2])
              
              return (
                <div 
                  key={index} 
                  className={isActive ? styles.analysisItemEmp : styles.analysisItem}
                >
                  <span className={isActive ? styles.analysisLabelEmp : styles.analysisLabel}>
                    {item.question}
                  </span>
                  <p className={styles.analysisText}>{item.answer}</p>
                </div>
              )
            })}
          </div>
        </section>

        {/* 결과 총평 */}
        <section className={styles.summarySection}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryHeader}>
              <span className={styles.summaryIcon}>
              </span>
              <h3 className={styles.summaryTitle}>결과 총평</h3>
            </div>
            <p className={styles.summaryText}>
              {summaryAnalysis || '분석 결과가 없습니다.'}
            </p>
          </div>
        </section>

        {/* AI 추천 */}
        <div className={styles.recommendationsHeader}>
          <div className={styles.recommendationsIcon}>
            <img src={aiChipIcon} alt="aiChip" />
            <h2 className={styles.recommendationsTitle} >AI 추천</h2>
          </div>
        </div>
        <section className={styles.recommendationsSection}>
          <div className={styles.tabBar}>
            <div className={styles.tabButtons}>
              <button
                className={`${styles.tabBtn} ${activeTab === 'places' ? styles.active : ''}`}
                onClick={() => setActiveTab('places')}
                type="button"
              >
                장소
              </button>
              <button
                className={`${styles.tabBtn} ${activeTab === 'music' ? styles.active : ''}`}
                onClick={() => setActiveTab('music')}
                type="button"
              >
                음악
              </button>
              <button
                className={`${styles.tabBtn} ${activeTab === 'videos' ? styles.active : ''}`}
                onClick={() => setActiveTab('videos')}
                type="button"
              >
                영상
              </button>
            </div>
          </div>

          <div className={styles.recommendationsRow}>
            <button type="button" className={styles.arrowBtn} onClick={prevTab} aria-label="이전">
              ◀
            </button>
            <div className={styles.recommendationsBody}>
                {activeTab === 'places' && (
                <>
                  {aiRecommendations.places.map((place, index) => {
                    const imageSrc =
                      place.image && place.image.trim() !== 'null'
                        ? place.image
                        : undefined 

                    return (
                      <div key={index} className={styles.recommendationCard}>
                        <div className={styles.placeCardInner}>
                          {/* 왼쪽: 대표 사진 */}
                          <div className={styles.placeImageWrapper}>
                            {imageSrc ? (
                              <img
                                src={imageSrc}
                                alt={place.title}
                                className={styles.placeImage}
                              />
                            ) : (
                              <div className={styles.placeImagePlaceholder}>이미지 없음</div>
                            )}
                          </div>

                          {/* 오른쪽: 내용 + 링크 */}
                          <div className={styles.placeContent}>
                            <h3 className={styles.placeTitle}>{place.title}</h3>
                            <p className={styles.placeRating}>⭐ {place.rating}</p>
                            <p className={styles.placeAddress}>{place.address}</p>

                            <a
                              href={place.url}
                              target="_blank"
                              rel="noreferrer"
                              className={styles.placeLink}
                            >
                              지도에서 바로가기
                            </a>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </>
              )}

              {activeTab === 'music' && (
                <>
                  {aiRecommendations.music.map((music, index) => {
                    const imageSrc =
                      music.image && music.image.trim() !== 'null'
                        ? music.image
                        : undefined 

                    return (
                      <div key={index} className={styles.recommendationCard}>
                        <div className={styles.placeCardInner}>
                          {/* 왼쪽: 대표 사진 */}
                          <div className={styles.musicImageWrapper}>
                            {imageSrc ? (
                              <img
                                src={imageSrc}
                                alt={music.title}
                                className={styles.musicImage}
                                onError={(e) => {
                                  e.currentTarget.onerror = null
                                  e.currentTarget.src = defaultCover
                                }}
                              />
                            ) : (
                              <div className={styles.placeImagePlaceholder}>이미지 없음</div>
                            )}
                          </div>

                          {/* 오른쪽: 내용 + 링크 */}
                          <div className={styles.placeContent}>
                            <h3 className={styles.placeTitle}>{music.title}</h3>
                            <p className={styles.placeRating}> {music.artist}</p>
                            <a
                              href={music.url}
                              target="_blank"
                              rel="noreferrer"
                              className={styles.placeLink}
                            >
                              감상 바로가기
                            </a>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </>
              )}

              {activeTab === 'videos' && (
                <>
                  {aiRecommendations.videos.map((videos, index) => {
                    const imageSrc =
                      videos.thumbnail && videos.thumbnail.trim() !== 'null'
                        ? videos.thumbnail
                        : undefined 

                    return (
                      <div key={index} className={styles.recommendationCard}>
                        <div className={styles.placeCardInner}>
                          {/* 왼쪽: 대표 사진 */}
                          <div className={styles.videoThumbWrapper}>
                            {imageSrc ? (
                              <img
                                src={imageSrc}
                                alt={videos.title}
                                className={styles.videoThumbnail}
                              />
                            ) : (
                              <div className={styles.placeImagePlaceholder}>이미지 없음</div>
                            )}
                          </div>

                          {/* 오른쪽: 내용 + 링크 */}
                          <div className={styles.placeContent}>
                            <h3 className={styles.placeTitle}>{videos.title}</h3>
                            <a
                              href={videos.url}
                              target="_blank"
                              rel="noreferrer"
                              className={styles.placeLink}
                            >
                              감상 바로가기
                            </a>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </>
              )}
            </div>
            <button type="button" className={styles.arrowBtn} onClick={nextTab} aria-label="다음">
              ▶
            </button>
          </div>
          
        </section>

        {/* 메인으로 돌아가기 버튼 */}
        <div className={styles.actionButtons}>
          {fromProgramResult && therapyProgramId ? (
            <button 
              className={styles.mainBtn} 
              onClick={() => navigate(`/programs/${therapyProgramId}/result`)}
            >
              뒤로가기
            </button>
          ) : healingCourseContext ? (
            <button 
              className={styles.mainBtn} 
              onClick={handleCompleteCourse}
              disabled={completingCourse}
            >
              {completingCourse
                ? '완료 처리 중...'
                : healingCourseContext?.title
                  ? `${healingCourseContext.title} 완료하기`
                  : '1일차 완료하기'}
            </button>
          ) : (
            <button 
              className={styles.mainBtn} 
              onClick={() => navigate('/gallery')}
            >
              갤러리로 이동
            </button>
          )}
        </div>
      </div>

      <AlertModal
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        message={alertMessage}
      />
    </div>
  )
}

export default ResultPage